import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useHasPermission } from '@/auth/auth-store';
import { useCreateFlow, useTerminalFlows } from '@/hooks/use-terminal-flows';
import { useT } from '@/i18n/use-t';
import type { FlowSurface } from './flow-vocab';

function extractError(err: unknown): string {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string } | undefined;
    return body?.message ?? err.message;
  }
  return 'Unexpected error';
}

/**
 * RFL-131 — flow-rules list. Each flow is a per-surface (optionally
 * per-location) ruleset terminals consult at FSM hook points. Create
 * here, then author rules in the editor (graphical or JSON).
 */
export function TerminalFlowsListPage() {
  const { data, isLoading } = useTerminalFlows();
  const create = useCreateFlow();
  const navigate = useNavigate();
  const canWrite = useHasPermission('terminal-flows.write');
  const t = useT('admin_ui');

  const [surface, setSurface] = useState<FlowSurface>('sco');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onCreate(): Promise<void> {
    setError(null);
    if (name.trim().length === 0) {
      setError(t('terminalFlows.list.nameRequired', 'Name is required.'));
      return;
    }
    try {
      const flow = await create.mutateAsync({ surface, name: name.trim(), rules: [] });
      navigate(`/terminal-flows/${flow.id}`);
    } catch (err) {
      setError(extractError(err));
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('terminalFlows.title', 'Flow rules')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'terminalFlows.list.intro',
            'Configure terminal FSM behaviour — e.g. “voiding a line on SCO requires attendant approval” — without code changes. Published rulesets sync to terminals.',
          )}
        </p>
      </header>

      {canWrite && (
        <section className="flex flex-wrap items-end gap-3 rounded-card border border-border bg-card p-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('terminalFlows.surface', 'Surface')}
            </label>
            <Select
              className="w-40"
              value={surface}
              onChange={(e) => setSurface(e.target.value as FlowSurface)}
              aria-label={t('terminalFlows.surface', 'Surface')}
            >
              <option value="sco">SCO</option>
              <option value="pos">POS</option>
              <option value="attendant">Attendant</option>
            </Select>
          </div>
          <div className="flex-1 space-y-1" style={{ minWidth: 240 }}>
            <label className="text-xs text-muted-foreground">
              {t('terminalFlows.name', 'Name')}
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('terminalFlows.list.namePlaceholder', 'e.g. Default SCO rules')}
              maxLength={200}
            />
          </div>
          <Button onClick={onCreate} disabled={create.isPending}>
            {create.isPending
              ? t('terminalFlows.list.creating', 'Creating…')
              : t('terminalFlows.list.create', 'New flow')}
          </Button>
        </section>
      )}

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      ) : data === undefined || data.flows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('terminalFlows.list.empty', 'No flow rulesets yet.')}
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>{t('terminalFlows.name', 'Name')}</TH>
              <TH>{t('terminalFlows.surface', 'Surface')}</TH>
              <TH>{t('terminalFlows.status', 'Status')}</TH>
              <TH className="text-right">{t('terminalFlows.rules', 'Rules')}</TH>
              <TH className="text-right">{t('terminalFlows.version', 'Version')}</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {data.flows.map((f) => (
              <TR key={f.id}>
                <TD>{f.name}</TD>
                <TD>
                  <Badge variant="outline">{f.surface}</Badge>
                </TD>
                <TD>
                  <Badge variant={f.status === 'published' ? 'success' : 'default'}>
                    {f.status}
                  </Badge>
                </TD>
                <TD className="text-right font-mono text-xs">{f.rules.length}</TD>
                <TD className="text-right font-mono text-xs">v{f.version}</TD>
                <TD className="text-right">
                  <Link
                    to={`/terminal-flows/${f.id}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('terminalFlows.open', 'Open')}
                  </Link>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
